import moment from 'moment';
import {
  getReservationChannelLabel,
  getReservationSourceLabel
} from '../../../shared/services/helpers/reservation-source-channel.helper';

export type AppliedFilterKey =
  | 'id'
  | 'cmId'
  | 'guestName'
  | 'guestEmail'
  | 'guestPhone'
  | 'city'
  | 'area'
  | 'unitTypeGroup'
  | 'channel'
  | 'source'
  | 'status'
  | 'unitType'
  | 'createdAt'
  | 'stay'
  | 'numberOfNights'
  | 'paymentAmount';

export interface AppliedFilterChip {
  key: AppliedFilterKey;
  label: string;
  value: string;
  /** Keys of the search query this chip represents; removing the chip deletes them. */
  queryKeys: string[];
}

type Option = { value: string; label: string };

export interface AppliedFilterLookups {
  cities?: Option[];
  areas?: { areaId: string; label: string }[];
  unitTypeGroups?: Option[];
  unitTypes?: Option[];
}

const formatDate = (ms: number) => moment(ms).format('D MMM YYYY');

const formatAmount = (amount: number) => amount.toLocaleString('en-US');

const labelFrom = (options: Option[] | undefined, value: string) =>
  options?.find((option) => option.value === value)?.label || value;

/**
 * Turns the search query the list was last fetched with into one chip per filter, so the
 * admin can see what is narrowing the list without opening the filters modal. Built from the
 * applied query rather than the modal's form state, so half-edited, unsearched values never
 * show up as active.
 */
export const buildAppliedFilterChips = (
  query: Record<string, any> | undefined,
  lookups: AppliedFilterLookups = {}
): AppliedFilterChip[] => {
  if (!query) return [];
  const chips: AppliedFilterChip[] = [];
  const add = (key: AppliedFilterKey, label: string, value: string, queryKeys: string[]) =>
    chips.push({ key, label, value, queryKeys });

  if (query.id) add('id', 'Reservation #', String(query.id), ['id']);
  if (query.cmId) add('cmId', 'Rentals United ID', String(query.cmId), ['cmId']);
  if (query.primaryGuestName) {
    add('guestName', 'Guest', String(query.primaryGuestName), ['primaryGuestName']);
  }
  if (query.primaryGuestEmail) {
    add('guestEmail', 'Email', String(query.primaryGuestEmail), ['primaryGuestEmail']);
  }
  if (query.primaryGuestPhone) {
    add('guestPhone', 'Phone', String(query.primaryGuestPhone), ['primaryGuestPhone']);
  }
  if (query.cityId) add('city', 'City', labelFrom(lookups.cities, query.cityId), ['cityId']);
  if (query.areaId) {
    const area = lookups.areas?.find((option) => option.areaId === query.areaId);
    add('area', 'Area', area?.label || String(query.areaId), ['areaId']);
  }
  if (query.unitTypeGroupId) {
    add(
      'unitTypeGroup',
      'UnitType Group',
      labelFrom(lookups.unitTypeGroups, query.unitTypeGroupId),
      ['unitTypeGroupId']
    );
  }
  if (query.channel) {
    add('channel', 'Channel', getReservationChannelLabel(query.channel), ['channel']);
  }
  if (query.source) add('source', 'Source', getReservationSourceLabel(query.source), ['source']);
  if (query.status) {
    add('status', 'Status', String(query.status).replace(/_/g, ' '), ['status']);
  }
  if (query.unitTypeId) {
    add('unitType', 'Unit Type', labelFrom(lookups.unitTypes, query.unitTypeId), ['unitTypeId']);
  }
  if (query.createdAtFrom && query.createdAtTo) {
    add(
      'createdAt',
      'Created',
      `${formatDate(query.createdAtFrom)} – ${formatDate(query.createdAtTo)}`,
      ['createdAtFrom', 'createdAtTo']
    );
  }
  if (query.checkInDate && query.checkOutDate) {
    add('stay', 'Stay', `${formatDate(query.checkInDate)} – ${formatDate(query.checkOutDate)}`, [
      'checkInDate',
      'checkOutDate'
    ]);
  }
  if (query.numberOfNights) {
    add('numberOfNights', 'Nights', String(query.numberOfNights), ['numberOfNights']);
  }
  const { min, max } = query.paymentAmountRange || {};
  if (min || max) {
    const value =
      min && max
        ? `${formatAmount(min)} – ${formatAmount(max)}`
        : min
        ? `≥ ${formatAmount(min)}`
        : `≤ ${formatAmount(max)}`;
    add('paymentAmount', 'Payment', value, ['paymentAmountRange']);
  }

  return chips;
};
